/**
 * Per-feed WebRTC: which tiles may use it, and when to renew the lease.
 *
 * The pure half of `useFeedWebrtc`, so the decisions can be tested without a
 * LiveKit server. The twin of `backend/livekit_rooms.py` — change a rule here and
 * you must change it there, the way `roiGeometry.ts` and `detection_roi.py` are
 * kept in step.
 *
 * WHY: MJPEG re-sends every pixel of every frame. Measured on the demo box at
 * 640x360, a tile is ~3.5 Mbit/s even after the fps/quality cut, so eight tiles
 * saturate an ordinary connection while the box itself sits near idle. The same
 * composited picture — overlays and detection boxes already burned in — is roughly
 * 0.6 Mbit/s over WebRTC, and the browser decodes it in hardware rather than
 * decoding JPEGs on the CPU. The cost is latency: 271 ms against MJPEG's 162.
 */
import { canCarryModel } from './aiChannels';

/**
 * Whether a tile can use WebRTC at all.
 *
 * Only feeds the SERVER reads, because a publisher sends the composited frame
 * and a browser-decoded feed never produces one. A phone never can: the backend
 * publishes it to LiveKit and never subscribes.
 *
 * An AIRCRAFT can, given its serial — its tile plays HLS, but the backend has
 * been reading its RTMP the whole flight. Exactly the same one-case widening as
 * `canCarryModel`, and deliberately the same predicate: a feed that can carry a
 * model is a feed with a composited picture to publish.
 */
export function canUseWebrtc(sourceType?: string, deviceSn?: string): boolean {
  return !!sourceType && canCarryModel(sourceType, deviceSn);
}

/**
 * How soon to ask again after a retryable failure.
 *
 * Much shorter than the renewal interval, and that gap is load-bearing. A tile's
 * first request usually 503s: the lease it just took is what starts the feed's
 * compositor, and the publisher only connects once a composited frame exists. So
 * the very next attempt normally succeeds. Retrying on the 30 s renewal clock
 * instead would leave every tile on MJPEG for half a minute after it opens, which
 * would read as the feature not working at all.
 */
export const RETRY_MS = 2_000;

/**
 * How long the last WebRTC frame may stay on screen while MJPEG reconnects.
 *
 * The backstop for the handback. If MJPEG never comes back we must stop showing a
 * frozen WebRTC frame regardless: a still picture reads as a live camera, which is
 * strictly worse than an honest NO SIGNAL card. Comfortably longer than an MJPEG
 * reconnect (the server's CONNECT_GRACE_SECONDS is 10 s, but a warm reader
 * delivers in well under one) and short enough that a stall is not mistaken for a
 * feed.
 */
export const HANDBACK_TIMEOUT_MS = 4_000;

export const MIN_RENEW_MS = 5_000;
export const MAX_RENEW_MS = 120_000;
export const DEFAULT_RENEW_MS = 30_000;

/**
 * How long to wait before renewing the lease.
 *
 * The server proposes a value; this clamps it. A server that answered 0 (or
 * something absurd) would otherwise turn the renewal into a request loop, and
 * every renewal re-runs the publisher sync on the backend.
 */
export function renewDelayMs(serverSeconds?: number): number {
  if (!serverSeconds || !Number.isFinite(serverSeconds) || serverSeconds <= 0) {
    return DEFAULT_RENEW_MS;
  }
  return Math.min(MAX_RENEW_MS, Math.max(MIN_RENEW_MS, serverSeconds * 1000));
}

/**
 * Whether a failed token request is worth retrying.
 *
 * 404 means this deployment has no LiveKit and 400/409 mean this feed is not
 * eligible — all three are permanent for as long as the tile is open, so retrying
 * is pure noise. A 503 is the publisher budget or a publisher still starting, and
 * that genuinely does clear. Anything else (a network blip, a 502 from nginx) is
 * worth one more try. In every case the tile is already showing MJPEG, so giving
 * up costs picture quality, never the picture.
 */
export function shouldRetry(status: number): boolean {
  return status !== 404 && status !== 400 && status !== 409;
}

/** A subscribe-only LiveKit lease for one feed. */
export interface FeedWebrtcCreds {
  livekitUrl: string;
  token: string;
  room: string;
}

/**
 * Which credentials the player should be holding — i.e. when a renewal is
 * allowed to restart a live WebRTC connection.
 *
 * THE PLAYER IS KEYED ON ITS CREDENTIALS. `ProcessedStreamPlayer` connects in an
 * effect over `[livekitUrl, token]` and cannot be keyed on the room instead,
 * because the room is *inside* the token — so the decision has to be made here,
 * where the room is still visible.
 *
 * The lease has to be renewed every 30 s or the backend correctly tears the
 * publisher down, and every renewal mints a fresh JWT (`nbf`/`exp` move with the
 * wall clock). Passing that straight through changed the player's deps and made
 * it disconnect and re-do ICE on a room that was working perfectly: measured on
 * the demo box, 1,125 viewer disconnects in six hours against 3 publisher ones,
 * every session ending at 26-30 s with `CLIENT_REQUEST_LEAVE`, ~1 s of black per
 * tile per renewal. In a grid the tiles renew on their own clocks, so it reads as
 * random tiles blinking. Same rule, and the same reason, as
 * `livekit_rooms.plan()` leaving an unchanged publisher strictly alone.
 *
 * `live` is what keeps this from being a one-way door: the player never
 * reconnects by itself, so a renewal arriving while it is DOWN is also the retry
 * that brings it back. Suppress that too and a tile which loses WebRTC once stays
 * on MJPEG until the operator changes source.
 */
export function stableCreds(
  held: FeedWebrtcCreds | null,
  incoming: FeedWebrtcCreds | null,
  live: boolean,
): FeedWebrtcCreds | null {
  if (!incoming) return null;          // no lease — MJPEG. Handback is the caller's.
  if (!held || !live) return incoming;
  if (held.room !== incoming.room) return incoming;
  if (held.livekitUrl !== incoming.livekitUrl) return incoming;
  return held;
}
